/**
 * 内页设备会话：仅存 sessionStorage，关 tab 即失效。
 * equipmentData 在会话活跃时同步写入 localStorage，供各 SDK 模块读取。
 */

import useHidGlobalStore, {
  type ConfigurationType,
} from "@/stores/hidGlobalStore";
import { useGlobalStore } from "@/stores/globalStore";
import { stopDeviceSessionPresence } from "@/utils/deviceSessionPresence";
import { bindDriverTabEquipment } from "@eweadn-qiankun/shared-utils";

const SESSION = {
  isConnecting: "eweadn:session:isConnecting",
  equipmentData: "eweadn:session:equipmentData",
  deviceInitialStatus: "eweadn:session:deviceInitialStatus",
  solutionRoviderName: "eweadn:session:solutionRoviderName",
  mouseCurrentStableId: "eweadn:session:mouseCurrentStableId",
} as const;

/** 各 SDK 仍从 localStorage 读取的 legacy key */
const LEGACY = {
  isConnecting: "isConnecting",
  equipmentData: "equipmentData",
  deviceInitialStatus: "deviceInitialStatus",
  solutionRoviderName: "solutionRoviderName",
  mouseCurrentStableId: "mouseCurrentStableId",
} as const;

const MIGRATION_KEY = "eweadn:deviceStorageMigration:v1";

function ssGet(key: string): string | null {
  try {
    return typeof window !== "undefined"
      ? window.sessionStorage.getItem(key)
      : null;
  } catch {
    return null;
  }
}

function ssSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

function ssRemove(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function lsRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/** 升级后清除旧版持久化设备列表，避免幽灵卡片 */
export function runDeviceStorageMigration(): void {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem(MIGRATION_KEY) === "1") return;
    window.localStorage.removeItem("app-hidGlobalStore");
    lsRemove(LEGACY.isConnecting);
    lsRemove(LEGACY.equipmentData);
    lsRemove(LEGACY.deviceInitialStatus);
    window.localStorage.setItem(MIGRATION_KEY, "1");
  } catch {
    // ignore
  }
}

export function isDeviceSessionActive(): boolean {
  return ssGet(SESSION.isConnecting) === "true";
}

export function setDeviceSessionActive(active: boolean): void {
  if (active) {
    ssSet(SESSION.isConnecting, "true");
    try {
      window.localStorage.setItem(LEGACY.isConnecting, "true");
    } catch {
      // ignore
    }
  } else {
    ssRemove(SESSION.isConnecting);
    lsRemove(LEGACY.isConnecting);
  }
}

function lsGet(key: string): string | null {
  try {
    return typeof window !== "undefined"
      ? window.localStorage.getItem(key)
      : null;
  } catch {
    return null;
  }
}

export function getEquipmentDataRaw(): string | null {
  return ssGet(SESSION.equipmentData) ?? lsGet(LEGACY.equipmentData);
}

export function getEquipmentData<T = Record<string, unknown>>(): T | null {
  const raw = getEquipmentDataRaw();
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function setEquipmentData(data: object): void {
  const json = JSON.stringify(data);
  ssSet(SESSION.equipmentData, json);
  bindDriverTabEquipment(data as Record<string, unknown>);
  try {
    window.localStorage.setItem(LEGACY.equipmentData, json);
  } catch {
    // ignore
  }
}

export function setDeviceInitialStatus(data: object): void {
  const json = JSON.stringify(data);
  ssSet(SESSION.deviceInitialStatus, json);
  try {
    window.localStorage.setItem(LEGACY.deviceInitialStatus, json);
  } catch {
    // ignore
  }
}

export function setSolutionRoviderName(name: string): void {
  ssSet(SESSION.solutionRoviderName, name);
  try {
    window.localStorage.setItem(LEGACY.solutionRoviderName, name);
  } catch {
    // ignore
  }
}

/**
 * 首页贝盈鼠 HID 建连：写入方案商供 getMouseSolutionName 路由 SDK。
 * 富芮坤/展立建连走 getMouseService().init(provider)，无需写此字段。
 * 内页会话进行中不写，避免后台建连抢改当前会话。
 */
export function setConnectProviderNameIfHome(name: string): void {
  if (isDeviceSessionActive()) return;
  setSolutionRoviderName(name);
}

/** 与 setSolutionRoviderName 对称：优先 session，再读 localStorage legacy */
export function getSolutionRoviderName(): string | null {
  return (
    ssGet(SESSION.solutionRoviderName) ?? lsGet(LEGACY.solutionRoviderName)
  );
}

export function setMouseCurrentStableId(stableId: string): void {
  ssSet(SESSION.mouseCurrentStableId, stableId);
  try {
    window.localStorage.setItem(LEGACY.mouseCurrentStableId, stableId);
  } catch {
    // ignore
  }
}

export function getMouseCurrentStableId(): string | null {
  return (
    ssGet(SESSION.mouseCurrentStableId) ?? lsGet(LEGACY.mouseCurrentStableId)
  );
}

/** F5 刷新后把 session 中的设备身份写回 zustand / globalStore，供 onRetry 与子应用 SDK 路由使用 */
export function restoreDeviceSessionToStore(): ConfigurationType | null {
  if (!isDeviceSessionActive()) return null;
  const eq = getEquipmentData<ConfigurationType>();
  if (!eq?.vendorId || !eq?.productId || !eq?.solutionRovider) {
    return null;
  }
  bindDriverTabEquipment(eq as Record<string, unknown>);
  const providerName =
    ssGet(SESSION.solutionRoviderName) ?? eq.solutionRovider ?? "";
  if (providerName) {
    setSolutionRoviderName(providerName);
  }
  const hid = useHidGlobalStore.getState();
  const current = hid.configurationNow;
  const sameSessionDevice =
    current?.vendorId === eq.vendorId &&
    current?.productId === eq.productId &&
    String(current?.solutionRovider ?? "") === String(eq.solutionRovider ?? "");
  if (!sameSessionDevice) {
    hid.setConfigurationNow(eq);
  }
  const global = useGlobalStore.getState();
  if (!global.equipmentData) {
    global.setEquipmentData(eq);
  }
  return eq;
}

/** 回首页或关会话：清除内页身份，保留 SDK 无关的 user 偏好 */
export function clearDeviceSession(): void {
  ssRemove(SESSION.isConnecting);
  ssRemove(SESSION.equipmentData);
  ssRemove(SESSION.deviceInitialStatus);
  ssRemove(SESSION.solutionRoviderName);
  ssRemove(SESSION.mouseCurrentStableId);
  lsRemove(LEGACY.isConnecting);
  lsRemove(LEGACY.equipmentData);
  lsRemove(LEGACY.deviceInitialStatus);
  lsRemove(LEGACY.solutionRoviderName);
  lsRemove(LEGACY.mouseCurrentStableId);
  stopDeviceSessionPresence();
}

const EMPTY_CONFIGURATION: ConfigurationType = {
  adaptation: [],
  displayname: null,
  keyboardType: null,
  productId: null,
  productName: null,
  solutionRovider: null,
  uuid: null,
  vendorId: null,
};

/**
 * 离开键盘/鼠标子应用进入主应用壳层（首页、社区等）：释放内页 SDK 绑定。
 * 社区分享/应用配置会通过 switchCommunityDevice 按所选设备重新绑定。
 */
export function releaseDeviceSessionForMainApp(): void {
  clearDeviceSession();
  try {
    useHidGlobalStore.getState().setConfigurationNow(EMPTY_CONFIGURATION);
  } catch {
    /* ignore */
  }
  try {
    const global = useGlobalStore.getState();
    global.setEquipmentData(null);
    global.setCurrentDeviceType("");
    global.setCurrentDeviceName("");
  } catch {
    /* ignore */
  }
}
